import React, { useState, useEffect } from 'react';
import { inventoriesApi, productsApi, depotsApi, locationsApi, stockApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/errorUtils';
import { 
  Plus, ClipboardList, CheckCircle, AlertTriangle,
  BarChart3, Package, Search, ArrowRight, ArrowLeft, Trash2, Info
} from 'lucide-react';

const InventoriesPage = () => {
  const [inventories, setInventories] = useState([]);
  const [products, setProducts] = useState([]);
  const [depots, setDepots] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [invSearch, setInvSearch] = useState('');
  const [invSortBy, setInvSortBy] = useState('created_at');
  const [invSortOrder, setInvSortOrder] = useState('desc');
  const [invStatusFilter, setInvStatusFilter] = useState('all');

  const [inventoryForm, setInventoryForm] = useState({
    depot_id: '',
    zone_id: '',
    inventory_type: 'complete',
    items: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [inventoriesRes, productsRes, depotsRes, stockRes] = await Promise.all([
        inventoriesApi.getAll(),
        productsApi.getAll({ limit: 500 }),
        depotsApi.getAll(),
        stockApi.getAll(),
      ]);

      setInventories(inventoriesRes.data);
      setProducts(productsRes.data.items || []);
      setDepots(depotsRes.data);
      setStockItems(stockRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleStartInventory = async () => {
    if (!inventoryForm.depot_id) {
      toast.error('Sélectionnez un dépôt');
      return;
    }

    // Get stock items for selected depot
    const depotStock = stockItems.filter(s => s.depot_id === inventoryForm.depot_id);
    
    // Prepare items for counting
    const items = depotStock.map(stock => {
      const product = products.find(p => p.id === stock.product_id);
      return {
        product_id: stock.product_id,
        product_reference: product?.reference || '',
        product_designation: product?.designation || '',
        location_id: stock.location_id || '',
        theoretical_quantity: stock.available_quantity || 0,
        counted_quantity: stock.available_quantity || 0, // Pre-fill with theoretical
      };
    });

    setInventoryForm({
      ...inventoryForm,
      items: items
    });
  };

  const updateCountedQuantity = (index, value) => {
    const newItems = [...inventoryForm.items];
    newItems[index] = { ...newItems[index], counted_quantity: parseInt(value) || 0 };
    setInventoryForm({ ...inventoryForm, items: newItems });
  };

  const handleCreateInventory = async (e) => {
    e.preventDefault();

    if (inventoryForm.items.length === 0) {
      toast.error('Aucun article à inventorier');
      return;
    }

    try {
      const data = {
        depot_id: inventoryForm.depot_id,
        zone_id: inventoryForm.zone_id || null,
        inventory_type: inventoryForm.inventory_type,
        items: inventoryForm.items.map(i => ({
          product_id: i.product_id,
          location_id: i.location_id || null,
          counted_quantity: i.counted_quantity,
        })),
      };

      await inventoriesApi.create(data);
      toast.success('Inventaire créé avec succès');
      setShowCreateDialog(false);
      fetchData();
      setInventoryForm({
        depot_id: '',
        zone_id: '',
        inventory_type: 'complete',
        items: [],
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création'));
    }
  };

  const handleValidateInventory = async (inventoryId) => {
    try {
      await inventoriesApi.validate(inventoryId);
      toast.success('Inventaire validé - Ajustements appliqués');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleDeleteInventory = async (inventoryId) => {
    if (!window.confirm('Supprimer cet inventaire ? Cette action est irréversible.')) return;
    try {
      await inventoriesApi.delete(inventoryId);
      toast.success('Inventaire supprimé');
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };

  const viewInventoryDetails = (inventory) => {
    setSelectedInventory(inventory);
    setShowDetailsDialog(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      in_progress: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      validated: 'bg-green-500/20 text-green-400 border-green-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    const labels = {
      in_progress: 'En cours',
      validated: 'Validé',
      cancelled: 'Annulé',
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.in_progress}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getTypeLabel = (type) => {
    const labels = {
      complete: 'Complet',
      rotating: 'Tournant',
      zone: 'Par zone',
    };
    return labels[type] || type;
  };

  // Calculate discrepancies
  const calculateDiscrepancies = (items) => {
    let totalItems = items?.length || 0;
    let itemsWithDiff = 0;
    let totalPositiveDiff = 0;
    let totalNegativeDiff = 0;

    items?.forEach(item => {
      const diff = (item.counted_quantity || 0) - (item.theoretical || 0);
      if (diff !== 0) {
        itemsWithDiff++;
        if (diff > 0) totalPositiveDiff += diff;
        else totalNegativeDiff += Math.abs(diff);
      }
    });

    return { totalItems, itemsWithDiff, totalPositiveDiff, totalNegativeDiff };
  };

  return (
    <div className="space-y-6" data-testid="inventories-page">
      {/* Info panel - explains the 3 inventory types */}
      <div className="bg-gradient-to-br from-cyan-500/5 to-amber-500/5 border border-zinc-800 rounded-lg p-4" data-testid="inventory-info-panel">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-md bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center flex-shrink-0">
            <Info className="w-4 h-4 text-cyan-400" />
          </div>
          <div className="flex-1 space-y-2">
            <h3 className="text-sm font-semibold text-white">Comment choisir le type d'inventaire ?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2.5">
                <div className="flex items-center gap-1.5 mb-1">
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]">Complet</Badge>
                </div>
                <p className="text-zinc-400 leading-relaxed">
                  Comptage exhaustif de <span className="text-white">tous les articles</span> d'un dépôt. À faire annuellement pour la clôture comptable.
                </p>
              </div>
              <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2.5">
                <div className="flex items-center gap-1.5 mb-1">
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px]">Tournant</Badge>
                </div>
                <p className="text-zinc-400 leading-relaxed">
                  Comptage <span className="text-white">partiel et régulier</span> d'une sélection d'articles (rotation). Permet de détecter les écarts en continu sans bloquer l'exploitation.
                </p>
              </div>
              <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2.5">
                <div className="flex items-center gap-1.5 mb-1">
                  <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30 text-[10px]">Par zone</Badge>
                </div>
                <p className="text-zinc-400 leading-relaxed">
                  Comptage ciblé d'une <span className="text-white">zone précise</span> du dépôt (allée, rayonnage). Utile après un réaménagement ou un incident localisé.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Inventaires</h1>
          <p className="text-zinc-400 text-sm mt-1">Contrôle et ajustement des stocks</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="create-inventory-button">
              <Plus className="w-4 h-4 mr-2" />
              Nouvel Inventaire
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">Nouvel Inventaire</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateInventory} className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Dépôt *</Label>
                  <Select 
                    value={inventoryForm.depot_id} 
                    onValueChange={(v) => setInventoryForm({...inventoryForm, depot_id: v, items: []})}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {depots.map(depot => (
                        <SelectItem key={depot.id} value={depot.id} className="text-white">
                          {depot.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Type d'inventaire</Label>
                  <Select 
                    value={inventoryForm.inventory_type} 
                    onValueChange={(v) => setInventoryForm({...inventoryForm, inventory_type: v})}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="complete" className="text-white" title="Comptage exhaustif de tous les articles du dépôt (clôture annuelle).">Complet</SelectItem>
                      <SelectItem value="rotating" className="text-white" title="Comptage partiel et régulier d'une sélection d'articles, en rotation, sans bloquer l'exploitation.">Tournant</SelectItem>
                      <SelectItem value="zone" className="text-white" title="Comptage ciblé d'une zone précise du dépôt (allée, rayonnage).">Par zone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 flex items-end">
                  <Button 
                    type="button" 
                    onClick={handleStartInventory}
                    variant="outline"
                    className="w-full border-zinc-700"
                    disabled={!inventoryForm.depot_id}
                  >
                    <Search className="w-4 h-4 mr-2" /> Charger les articles
                  </Button>
                </div>
              </div>

              {/* Items to count */}
              {inventoryForm.items.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-zinc-300">{inventoryForm.items.length} articles à inventorier</Label>
                    <Badge variant="outline">
                      {inventoryForm.items.filter(i => i.counted_quantity !== i.theoretical_quantity).length} écarts
                    </Badge>
                  </div>
                  
                  <ScrollArea className="h-[400px] rounded-lg border border-zinc-800">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-zinc-800">
                          <TableHead className="text-zinc-400">Référence</TableHead>
                          <TableHead className="text-zinc-400">Désignation</TableHead>
                          <TableHead className="text-zinc-400 text-center">Théorique</TableHead>
                          <TableHead className="text-zinc-400 text-center">Compté</TableHead>
                          <TableHead className="text-zinc-400 text-center">Écart</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {inventoryForm.items.map((item, index) => {
                          const diff = item.counted_quantity - item.theoretical_quantity;
                          return (
                            <TableRow key={index} className="border-zinc-800">
                              <TableCell className="font-mono text-cyan-400">{item.product_reference}</TableCell>
                              <TableCell className="text-white">{item.product_designation?.substring(0, 30)}</TableCell>
                              <TableCell className="text-center text-zinc-400">{item.theoretical_quantity}</TableCell>
                              <TableCell className="text-center">
                                <Input
                                  type="number"
                                  value={item.counted_quantity}
                                  onChange={(e) => updateCountedQuantity(index, e.target.value)}
                                  className="w-20 mx-auto bg-zinc-950 border-zinc-700 text-white text-center"
                                  min="0"
                                />
                              </TableCell>
                              <TableCell className="text-center">
                                {diff !== 0 && (
                                  <Badge variant="outline" className={diff > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                                    {diff > 0 ? '+' : ''}{diff}
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="border-zinc-700">
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="bg-amber-500 hover:bg-amber-400 text-black"
                  disabled={inventoryForm.items.length === 0}
                >
                  <ClipboardList className="w-4 h-4 mr-2" /> Enregistrer l'inventaire
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search & Sort Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input value={invSearch} onChange={e => setInvSearch(e.target.value)}
            placeholder="Rechercher par ID, depot, type..."
            className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-amber-500/50 focus:outline-none"
            data-testid="inventory-search" />
        </div>
        <select value={invStatusFilter} onChange={e => setInvStatusFilter(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-xs text-zinc-300">
          <option value="all">Tous statuts</option>
          <option value="completed">Termine</option>
          <option value="in_progress">En cours</option>
          <option value="pending">En attente</option>
        </select>
        <span className="text-zinc-600 text-xs">Tri:</span>
        <select value={invSortBy} onChange={e => setInvSortBy(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-xs text-zinc-300">
          <option value="created_at">Date</option>
          <option value="id">ID</option>
          <option value="inventory_type">Type</option>
        </select>
        <button onClick={() => setInvSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
          className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-xs text-zinc-300 hover:border-amber-500/50">
          {invSortOrder === 'asc' ? '↑ A-Z' : '↓ Z-A'}
        </button>
      </div>

      {/* Inventories List */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-0">
          <ScrollArea className="h-[calc(100vh-340px)]">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400">ID</TableHead>
                  <TableHead className="text-zinc-400">Type</TableHead>
                  <TableHead className="text-zinc-400">Dépôt</TableHead>
                  <TableHead className="text-zinc-400">Date</TableHead>
                  <TableHead className="text-zinc-400 text-center">Articles</TableHead>
                  <TableHead className="text-zinc-400 text-center">Écarts</TableHead>
                  <TableHead className="text-zinc-400">Statut</TableHead>
                  <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : (() => {
                  let filtered = inventories;
                  if (invSearch) {
                    const q = invSearch.toLowerCase();
                    filtered = filtered.filter(i =>
                      i.id?.toLowerCase().includes(q) ||
                      (i.depot_name || '').toLowerCase().includes(q) ||
                      (i.inventory_type || '').toLowerCase().includes(q)
                    );
                  }
                  if (invStatusFilter !== 'all') filtered = filtered.filter(i => i.status === invStatusFilter);
                  filtered = [...filtered].sort((a, b) => {
                    let va = a[invSortBy] || '', vb = b[invSortBy] || '';
                    if (invSortBy === 'created_at') { va = new Date(va || 0); vb = new Date(vb || 0); }
                    if (va < vb) return invSortOrder === 'asc' ? -1 : 1;
                    if (va > vb) return invSortOrder === 'asc' ? 1 : -1;
                    return 0;
                  });
                  if (filtered.length === 0) return (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-zinc-500">
                      <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-50" /><p>Aucun inventaire</p>
                    </TableCell></TableRow>
                  );
                  return filtered.map((inv) => {
                    const stats = calculateDiscrepancies(inv.discrepancies);
                    return (
                      <TableRow key={inv.id} className="border-zinc-800 table-row-hover">
                        <TableCell className="font-mono text-cyan-400">{inv.id}</TableCell>
                        <TableCell className="text-white">{getTypeLabel(inv.inventory_type)}</TableCell>
                        <TableCell className="text-zinc-400">{inv.depot_name || depots.find(d => d.id === inv.depot_id)?.name || inv.depot_id?.slice(0, 12)}</TableCell>
                        <TableCell className="text-zinc-400">
                          {new Date(inv.created_at).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell className="text-center text-white">{inv.items?.length || 0}</TableCell>
                        <TableCell className="text-center">
                          {inv.discrepancies?.length > 0 ? (
                            <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                              {inv.discrepancies.length}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
                              0
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(inv.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-zinc-700"
                              onClick={() => viewInventoryDetails(inv)}
                            >
                              Détails
                            </Button>
                            {inv.status === 'in_progress' && (
                              <Button
                                size="sm"
                                onClick={() => handleValidateInventory(inv.id)}
                                className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" /> Valider
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                              onClick={() => handleDeleteInventory(inv.id)}
                              data-testid={`delete-inventory-${inv.id}`}
                              title="Supprimer l'inventaire"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">
              Détails de l'inventaire {selectedInventory?.id}
            </DialogTitle>
          </DialogHeader>
          {selectedInventory && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">Type</p>
                  <p className="text-lg font-semibold text-white">{getTypeLabel(selectedInventory.inventory_type)}</p>
                </div>
                <div className="p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">Articles</p>
                  <p className="text-lg font-semibold text-white">{selectedInventory.items?.length || 0}</p>
                </div>
                <div className="p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">Écarts</p>
                  <p className={`text-lg font-semibold ${selectedInventory.discrepancies?.length > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                    {selectedInventory.discrepancies?.length || 0}
                  </p>
                </div>
              </div>

              {selectedInventory.discrepancies?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-amber-400">Écarts détectés</h4>
                  <ScrollArea className="h-[200px]">
                    {selectedInventory.discrepancies.map((disc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-zinc-950 rounded-lg border border-zinc-800 mb-2">
                        <div>
                          <span className="font-mono text-cyan-400">{disc.product_id?.slice(0, 12)}</span>
                          {disc.location_id && (
                            <span className="text-zinc-500 ml-2">@ {disc.location_id}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-zinc-400">{disc.theoretical}</span>
                          <ArrowRight className="w-4 h-4 text-zinc-600" />
                          <span className="text-white">{disc.counted}</span>
                          <Badge variant="outline" className={disc.difference > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                            {disc.difference > 0 ? '+' : ''}{disc.difference}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoriesPage;
